import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Briefcase, Plus, Edit2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DesignationMaster({ user, onLogout }) {
  const [designations, setDesignations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDesig, setEditingDesig] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    level: 'Mid-Level',
    description: ''
  });

  const LEVELS = ['Junior', 'Mid-Level', 'Senior', 'Management', 'Executive'];

  useEffect(() => {
    fetchDesignations();
  }, []);

  const fetchDesignations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/designations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDesignations(response.data.designations || []);
    } catch (error) {
      console.error('Failed to fetch designations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingDesig) {
        await axios.put(`${API}/master-data/designations/${editingDesig.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Designation updated successfully!');
      } else {
        await axios.post(`${API}/master-data/designations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Designation created successfully!');
      }

      resetForm();
      fetchDesignations();
    } catch (error) {
      alert('Failed to save designation: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (desig) => {
    setEditingDesig(desig);
    setFormData({
      title: desig.title,
      department: desig.department || '',
      level: desig.level || 'Mid-Level',
      description: desig.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this designation?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/master-data/designations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Designation deleted successfully!');
      fetchDesignations();
    } catch (error) {
      alert('Failed to delete designation: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      department: '',
      level: 'Mid-Level',
      description: ''
    });
    setEditingDesig(null);
    setShowForm(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>
              Designation Master
            </h1>
            <p className="text-gray-600 mt-1">Manage job titles and positions</p>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) resetForm();
            }}
            className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
            style={{backgroundColor: '#2b3380'}}
          >
            <Plus className="w-5 h-5" />
            {showForm ? 'Cancel' : 'Add Designation'}
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>
              {editingDesig ? 'Edit Designation' : 'Add New Designation'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Job Title *</label>
                  <input
                    type="text"
                    placeholder="e.g., Senior Software Engineer"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Level</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({...formData, level: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Department</label>
                  <input
                    type="text"
                    placeholder="e.g., Engineering, Sales"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    placeholder="Brief description of role responsibilities"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-6 py-2 rounded-lg text-white"
                  style={{backgroundColor: '#2b3380'}}
                >
                  {editingDesig ? 'Update Designation' : 'Create Designation'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 rounded-lg border"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {designations.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow-md p-8 text-center">
              <Briefcase className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500">No designations configured yet</p>
            </div>
          ) : (
            designations.map((desig) => (
              <div key={desig.id} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-6 h-6" style={{color: '#2b3380'}} />
                    <h3 className="text-lg font-bold" style={{color: '#2b3380'}}>
                      {desig.title}
                    </h3>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    desig.level === 'Executive' ? 'bg-purple-100 text-purple-800' :
                    desig.level === 'Management' ? 'bg-blue-100 text-blue-800' :
                    desig.level === 'Senior' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {desig.level}
                  </span>
                </div>

                {desig.department && (
                  <p className="text-sm font-medium text-gray-700 mb-2">{desig.department}</p>
                )}

                {desig.description && (
                  <p className="text-sm text-gray-600 mb-4">{desig.description}</p>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(desig)}
                    className="flex-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(desig.id)}
                    className="flex-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

export default DesignationMaster;